/**
 * voice.js — optional voice cues via the browser SpeechSynthesis API.
 * ============================================================================
 * No external/audio files. If speech is unavailable the test still works with
 * beeps + on-screen text only (callers must never depend on speech succeeding).
 *
 * iOS / Safari notes (this is what makes voice actually audible on iPhone PWA):
 *  - The VERY FIRST speak() must be triggered synchronously inside a real user
 *    gesture, otherwise iOS silently blocks all later speech. We expose
 *    primeSpeech() to be called from the Start-button handler (see unlock()).
 *  - iOS sometimes leaves the synth in a "paused" state → resume() before speak.
 *  - getVoices() is async; we warm it up and pick an English voice when asked.
 */
const supported = typeof window !== 'undefined' && 'speechSynthesis' in window;

let voicesCache = [];
let primed = false;

function loadVoices() {
  if (!supported) return [];
  try {
    const v = window.speechSynthesis.getVoices();
    if (v && v.length) voicesCache = v;
  } catch { /* noop */ }
  return voicesCache;
}

if (supported) {
  loadVoices();
  try { window.speechSynthesis.onvoiceschanged = loadVoices; } catch { /* noop */ }
}

export const voiceSupported = () => typeof window !== 'undefined' && !!(window.AudioContext || window.webkitAudioContext);

function pickVoice(lang) {
  const list = voicesCache.length ? voicesCache : loadVoices();
  if (!list.length) return null;
  const want = (lang || 'en-US').toLowerCase();
  return (
    list.find((v) => v.lang && v.lang.toLowerCase() === want) ||
    list.find((v) => v.lang && v.lang.toLowerCase().startsWith(want.slice(0, 2))) ||
    null
  );
}

/**
 * Prime SpeechSynthesis inside a user gesture so iOS will allow later speech.
 * Must be called SYNCHRONOUSLY from the click/tap handler (no awaits before it).
 */
export function primeSpeech() {
  if (!supported) return false;
  try {
    window.speechSynthesis.cancel();
    window.speechSynthesis.resume();
    loadVoices();
    const u = new SpeechSynthesisUtterance(' ');
    u.volume = 0;
    u.rate = 2;
    window.speechSynthesis.speak(u);
    primed = true;
    return true;
  } catch {
    return false;
  }
}

export function speak(text, { lang = 'sv-SE', rate = 1.04, volume = 1, pitch = 1 } = {}) {
  if (!supported || !text) return false;
  try {
    // iOS can leave the queue paused between utterances — nudge it back.
    if (window.speechSynthesis.paused) window.speechSynthesis.resume();
    const u = new SpeechSynthesisUtterance(String(text));
    u.lang = lang;
    u.rate = rate;
    u.volume = volume;
    u.pitch = pitch;
    const v = pickVoice(lang);
    if (v) u.voice = v;
    window.speechSynthesis.speak(u);
    return true;
  } catch {
    return false;
  }
}

export function cancelSpeech() {
  if (!supported) return;
  try { window.speechSynthesis.cancel(); } catch { /* noop */ }
}

export const isSpeechPrimed = () => primed;
